import { LOG_LEVELS, logMessage } from '../logging.js';
import stateManager from '../data/state_manager.js';
import { sendAuthenticatedRequest } from '../auth.js';
import config from '../../config/index.js';

class DropzoneManager {
  constructor(dropzone_id, url, message, headers) {
    this.isMobileScanner = dropzone_id === '#mobile-dropzone1';
    this.dropzoneInstance = this.createDropzone(
      dropzone_id,
      url,
      message,
      headers
    );
    this.setupDropzoneUI();
    this.headers = headers;
  }

  instance() {
    return this.dropzoneInstance;
  }

  addFile(file, preview = null) {
    this.removeAllFiles();
    if (preview) {
      file.cardPreview = preview;
      file.width = preview.width;
      file.height = preview.height;
    }
    this.dropzoneInstance.addFile(file);
  }

  openFilePicker() {
    this.dropzoneInstance.hiddenFileInput?.click();
  }

  setupDropzoneUI() {
    const dropzoneContainer = this.dropzoneInstance.element.parentNode;
    if (!this.isMobileScanner) {
      let buttonGroup = dropzoneContainer.querySelector('.button-group');
      if (!buttonGroup) {
        buttonGroup = document.createElement('div');
        buttonGroup.className = 'button-group';
        dropzoneContainer.appendChild(buttonGroup);

        this.rotateLeftButton = this.createElementWithAttributes('button', {
          innerHTML:
            '<i class="fas fa-undo rotate-button-icon"></i>Rotate Left',
          className: 'rotate-button',
        });
        this.rotateRightButton = this.createElementWithAttributes('button', {
          innerHTML:
            '<i class="fas fa-redo rotate-button-icon"></i>Rotate Right',
          className: 'rotate-button',
        });

        this.rotateLeftButton.disabled = true;
        this.rotateRightButton.disabled = true;

        buttonGroup.appendChild(this.rotateLeftButton);
        buttonGroup.appendChild(this.rotateRightButton);
      }
    }

    this.attachEventListeners();

    this.dropzoneInstance.on('error', (file, response) => {
      let errorMessage;

      if (!response) {
        // No response from the server
        errorMessage =
          'The server did not respond. Please check your internet connection or try again later.';
      } else if (typeof response === 'object' && response.errorCode) {
        // Handle known error codes from the backend
        switch (response.errorCode) {
          case 'missing_token':
            errorMessage =
              'Authentication token is missing. Please log in and try again.';
            break;
          case 'invalid_token':
            errorMessage = 'Invalid authentication token. Please log in again.';
            break;
          case 'token_expired':
            errorMessage =
              'Your authentication token has expired. Please refresh the page and try again. If the error persists, please log out and log back on.';
            break;
          case 'server_error':
            errorMessage = 'A server error occurred. Please try again later.';
            break;
          default:
            errorMessage =
              'An unknown error occurred while uploading the file. Please try again.';
        }
      } else if (typeof response === 'object' && response.error) {
        // Use the error message from the server
        errorMessage = response.error;
      } else if (typeof response === 'string') {
        // Handle errorMessage as a simple string
        errorMessage = response;
      } else {
        // Fallback if response structure is unexpected
        errorMessage =
          'An unknown error occurred without a proper error message. Please try again.';
      }

      // Display the error message to the user
      alert(errorMessage); // Replace with a non-blocking notification system if preferred

      // Log the error for debugging
      logMessage(
        LOG_LEVELS.ERROR,
        'Error loading Dropzone file:',
        response || errorMessage
      );

      // Update application state
      stateManager.updateState({ stopProcessing: true });

      // Optional: Remove the file preview to keep UI clean
      this.dropzoneInstance.removeFile(file);
    });
  }

  createDropzone(id, url, defaultMessage, headers) {
    const isMobileScanner = id === '#mobile-dropzone1';
    const mobileCardGuide = isMobileScanner
      ? `
        <div class="mobile-fallback-card-frame" aria-hidden="true">
          <span>Keep the full card inside this shape</span>
        </div>
      `
      : '';
    const messageWithList = `
    <div class="dropzone-message">
      ${mobileCardGuide}
      <p>${defaultMessage}</p>
      <ul class="dz-tips-list">
        <li class="dz-tip-item">Place the card on a smooth, flat surface</li>
        <li class="dz-tip-item">Avoid glare</li>
        <li class="dz-tip-item">Avoid blurry photos</li>
        <li class="dz-tip-item">Leave a narrow border around the card</li>
      </ul>
    </div>
  `;

    const options = {
      url: url,
      headers: headers,
      paramName: 'image',
      maxFilesize: 10,
      maxFiles: 1,
      acceptedFiles: 'image/*',
      dictDefaultMessage: messageWithList,
      clickable: true,
      addRemoveLinks: false,
      thumbnailWidth: 1200,
      thumbnailHeight: null,
      thumbnailMethod: 'contain',
      autoProcessQueue: false,
    };

    return new Dropzone(id, options);
  }

  attachEventListeners() {
    this.dropzoneInstance.on('thumbnail', (file) => this.handleThumbnail(file));
    this.dropzoneInstance.on('removedfile', (file) =>
      this.handleRemovedFile(file)
    );
  }

  handleThumbnail(file) {
    this.file = file;
    this.applyCustomPreview(file);
    this.updateMobileReviewLayout(file);
    const { dropzoneFiles } = stateManager.getState();
    stateManager.updateState({ dropzoneFiles: dropzoneFiles + 1 });

    this.warmup();
    this.hideProgressElement(file);
    const deleteButton = this.createDeleteButton();
    this.setupButtonPositions(deleteButton);
    this.setupRotationButtons(file.previewElement, deleteButton);
    this.appendDeleteButton(file.previewElement, deleteButton);
    this.attachDeleteEvent(deleteButton, file);
    this.updatePreviewDimensions(file.previewElement, deleteButton);
  }

  applyCustomPreview(file) {
    if (!file.cardPreview?.url) {
      return;
    }

    const image = file.previewElement?.querySelector('.dz-image img');
    if (!image) {
      return;
    }

    image.src = file.cardPreview.url;
    image.addEventListener(
      'load',
      () => {
        URL.revokeObjectURL(file.cardPreview.url);
        file.cardPreview.url = null;
      },
      { once: true }
    );
  }

  updateMobileReviewLayout(file) {
    if (!this.isMobileScanner || !file.width || !file.height) {
      return;
    }

    const dropzoneElement = this.dropzoneInstance.element;
    const isLandscape = file.width > file.height;
    dropzoneElement.classList.add('mobile-card-review');
    dropzoneElement.classList.toggle('is-landscape', isLandscape);
    dropzoneElement.style.aspectRatio = `${file.width} / ${file.height}`;
    dropzoneElement.style.height = 'auto';
  }

  hideProgressElement(file) {
    const progressElement = file.previewElement.querySelector('.dz-progress');
    if (progressElement) {
      progressElement.style.display = 'none';
    }
  }

  updatePreviewDimensions(previewElement, deleteButton) {
    previewElement.style.display = 'block';

    const dzImage = previewElement.querySelector('.dz-image');

    const setDimensions = () => {
      // Always constrain the preview element to the dropzone size so
      // images never overflow the container.
      previewElement.style.height = '100%';
      previewElement.style.width = '100%';
      dzImage.style.width = '100%';
      dzImage.style.height = '100%';

      if (deleteButton) {
        this.centerDeleteButton(previewElement, deleteButton);
      }
    };

    const img = previewElement.querySelector('img');
    if (img.complete) {
      setDimensions();
    } else {
      img.addEventListener('load', () => {
        setDimensions();
      });
    }
  }

  centerDeleteButton(previewElement, deleteButton) {
    const img = previewElement.querySelector('img');
    const dzImage = previewElement.querySelector('.dz-image');

    const reposition = () => {
      const offsetX = (dzImage.clientWidth - img.clientWidth) / 2;
      const offsetY = (dzImage.clientHeight - img.clientHeight) / 2;

      if (deleteButton.style.left !== '') {
        deleteButton.style.left = `${offsetX}px`;
      }
      if (deleteButton.style.right !== '') {
        deleteButton.style.right = `${offsetX}px`;
      }
      if (deleteButton.style.top !== '') {
        deleteButton.style.top = `${offsetY}px`;
      }
      if (deleteButton.style.bottom !== '') {
        deleteButton.style.bottom = `${offsetY}px`;
      }
    };

    if (img.complete) {
      reposition();
    } else {
      img.addEventListener('load', reposition, { once: true });
    }
  }

  createDeleteButton() {
    return this.createElementWithAttributes('button', {
      innerHTML: '<i class="fas fa-times"></i>',
      className: 'delete-button dz-remove',
    });
  }

  setupButtonPositions(button) {
    button.style.left = '0px';
    button.style.top = '0px';
  }

  setupRotationButtons(previewElement, deleteButton) {
    if (!this.rotateLeftButton || !this.rotateRightButton) {
      return;
    }

    this.rotateLeftButton.disabled = false;
    this.rotateRightButton.disabled = false;

    this.rotateLeftButton.addEventListener('click', () => {
      this.rotateImage(previewElement, -90);
      this.rotateButtonLeft(deleteButton);
    });
    this.rotateRightButton.addEventListener('click', () => {
      this.rotateImage(previewElement, 90);
      this.rotateButtonRight(deleteButton);
    });
  }

  appendDeleteButton(previewElement, deleteButton) {
    previewElement.appendChild(deleteButton);
  }

  attachDeleteEvent(deleteButton, file) {
    deleteButton.addEventListener('click', () => {
      this.dropzoneInstance.removeFile(file);
    });
  }

  async processFiles(skipCornerDetection) {
    const files = this.dropzoneInstance.getAcceptedFiles();
    for (const file of files) {
      const formData = new FormData();
      formData.append(this.dropzoneInstance.options.paramName, file);

      const rotationDegrees =
        file.previewElement.getAttribute('data-rotation') || '0';
      formData.append('rotationDegrees', rotationDegrees);

      // Add skipCornerDetection to the form data
      formData.append('skipCornerDetection', String(skipCornerDetection));
      formData.append('maxCardCount', '1');

      try {
        const response = await sendAuthenticatedRequest(
          this.dropzoneInstance.options.url,
          {
            method: 'POST',
            body: formData,
          }
        );

        if (response.ok) {
          this.dropzoneInstance.emit('success', file, response.body);
        } else {
          this.dropzoneInstance.emit('error', file, response.body);
        }
      } catch (error) {
        this.dropzoneInstance.emit('error', file, error.message);
      }
    }
  }

  async warmup() {
    try {
      await sendAuthenticatedRequest(`${config.detectionUrl}/warmup`, {
        method: 'POST',
      });
    } catch (error) {
      console.log(error.message);
    }
  }

  handleRemovedFile(file) {
    file.previewElement.style.height = null;
    if (this.isMobileScanner) {
      this.dropzoneInstance.element.classList.remove(
        'mobile-card-review',
        'is-landscape'
      );
      this.dropzoneInstance.element.style.aspectRatio = '';
      this.dropzoneInstance.element.style.height = '';
    }
    const { dropzoneFiles } = stateManager.getState();
    const newDropZoneFiles = dropzoneFiles - 1;

    if (this.rotateLeftButton && this.rotateRightButton) {
      this.rotateLeftButton.disabled = true;
      this.rotateRightButton.disabled = true;
    }
    this.file = null;

    stateManager.updateState({ dropzoneFiles: newDropZoneFiles });
  }

  rotateButtonLeft(button) {
    if (button.style.left && button.style.top) {
      button.style.right = '0px';
      button.style.left = null;
    } else if (button.style.right && button.style.top) {
      button.style.bottom = '0px';
      button.style.top = null;
    } else if (button.style.bottom && button.style.right) {
      button.style.left = '0px';
      button.style.right = null;
    } else if (button.style.bottom && button.style.left) {
      button.style.top = '0px';
      button.style.bottom = null;
    }
    this.centerDeleteButton(button.parentElement, button);
  }

  rotateButtonRight(button) {
    if (button.style.left && button.style.top) {
      button.style.bottom = '0px';
      button.style.top = null;
    } else if (button.style.bottom && button.style.left) {
      button.style.right = '0px';
      button.style.left = null;
    } else if (button.style.bottom && button.style.right) {
      button.style.top = '0px';
      button.style.bottom = null;
    } else if (button.style.right && button.style.top) {
      button.style.left = '0px';
      button.style.right = null;
    }
    this.centerDeleteButton(button.parentElement, button);
  }

  createElementWithAttributes(tagName, attributes) {
    const element = document.createElement(tagName);
    for (let key in attributes) {
      element[key] = attributes[key];
    }
    return element;
  }

  removeAllFiles() {
    this.dropzoneInstance.removeAllFiles(true);
  }

  rotateImage(imgElement, degrees) {
    const currentDegrees = parseInt(
      imgElement.getAttribute('data-rotation') || '0'
    );
    const newRotation = currentDegrees + degrees;
    imgElement.style.transform = `rotate(${newRotation}deg)`;
    imgElement.setAttribute('data-rotation', newRotation);
  }

  getRotation() {
    const previewElement = this.file.previewElement;

    const currentDegrees = parseInt(
      previewElement.getAttribute('data-rotation') || '0'
    );
    return currentDegrees;
  }
}

export { DropzoneManager };
